import type { StockAvailabilityDto } from "@/api/endpoints";
import { formatMonthYear } from "@/shared/datetime";

/**
 * Batch selection rules, shared by every screen that dispenses stock — OP/IP billing, ward issue,
 * OT consumption. They agree on FEFO, on what counts as dispensable, and on how expiry is worded,
 * so the same batch is offered wherever a pharmacist stands.
 */

/** Days from today to the given expiry date; negative once it has passed. */
export function daysToExpiry(iso?: string | null): number | null {
  if (!iso) return null;
  const exp = new Date(iso);
  if (Number.isNaN(exp.getTime())) return null;
  const today = new Date();
  return Math.floor((exp.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)) / 86_400_000);
}

/** Anything expiring inside this window is dispensable but flagged. Matches the near-expiry queue. */
export const NEAR_EXPIRY_DAYS = 90;

export function expiryLabel(iso?: string | null): string {
  return iso ? formatMonthYear(iso) : "no expiry";
}

/** Colour + wording for a batch's expiry. */
export function expiryState(iso?: string | null): { label: string; color: "error" | "warning" | "text.secondary" } {
  const d = daysToExpiry(iso);
  if (d === null) return { label: "No expiry recorded", color: "text.secondary" };
  if (d < 0) return { label: `Expired ${expiryLabel(iso)}`, color: "error" };
  if (d <= NEAR_EXPIRY_DAYS) return { label: `Expires ${expiryLabel(iso)} — ${d} day(s) left`, color: "warning" };
  return { label: `Expires ${expiryLabel(iso)}`, color: "text.secondary" };
}

/** Dispensable = has stock and has not expired. Expired stock is written off, never issued. */
export function dispensable(b: StockAvailabilityDto): boolean {
  return b.availableQty > 0 && (daysToExpiry(b.expiryDate) ?? 1) >= 0;
}

/** Earliest expiry first; batches with no expiry date sort last. */
export function byFefo(a: StockAvailabilityDto, b: StockAvailabilityDto): number {
  if (!a.expiryDate) return b.expiryDate ? 1 : 0;
  if (!b.expiryDate) return -1;
  return a.expiryDate.localeCompare(b.expiryDate);
}

/**
 * FEFO with a nudge for practicality: earliest expiry first, but a batch that cannot cover the
 * quantity — or has already expired — is not chosen for the operator. Expired stock is never
 * auto-selected; it stays pickable only so a supervisor can see it in the list.
 */
export function pickDefaultBatch(batches: StockAvailabilityDto[], qty: number): string {
  const usable = batches.filter(b => (daysToExpiry(b.expiryDate) ?? 1) >= 0);
  return (usable.find(b => b.availableQty >= qty) ?? usable[0])?.batchId ?? "";
}

/**
 * Walk batches earliest-expiry-first taking what each can cover, until the quantity is met.
 * Returns the per-batch plan plus whatever could not be covered.
 */
export function allocateFefo(batches: StockAvailabilityDto[], qty: number) {
  const plan: { batchId: string; batchNo: string; qty: number }[] = [];
  let remaining = qty;
  for (const b of batches.filter(dispensable).sort(byFefo)) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, b.availableQty);
    plan.push({ batchId: b.batchId, batchNo: b.batchNo, qty: take });
    remaining -= take;
  }
  return { plan, shortfall: Math.max(0, remaining) };
}
